import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { authClient } from "@/lib/auth";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

type ApprovalStatus = "approved" | "expired" | "pending" | "rejected";

type RequestDetails = {
  expiresAt: string;
  id: string;
  requestedMachineName: string | null;
  status: ApprovalStatus;
};

type OrganizationOption = {
  id: string;
  name: string;
};

function extractApprovalStatus(args: { payload: Record<string, unknown> | null }): ApprovalStatus | null {
  const status = args.payload?.status;
  if (
    status === "approved" ||
    status === "expired" ||
    status === "pending" ||
    status === "rejected"
  ) {
    return status;
  }
  return null;
}

export const Route = createFileRoute("/cli/authorize")({
  component: CliAuthorizePage,
  validateSearch: (search: Record<string, unknown>) => ({
    requestId: typeof search.requestId === "string" ? search.requestId : "",
    userCode: typeof search.userCode === "string" ? search.userCode : "",
  }),
});

function buildRequestApiUrl(args: { path: string; requestId: string; userCode: string }): string {
  return `${API_BASE_URL}/api/cli/auth/requests/${encodeURIComponent(args.requestId)}${args.path}?userCode=${encodeURIComponent(args.userCode)}`;
}

function CliAuthorizePage() {
  const { requestId, userCode } = Route.useSearch();
  const { data: session, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const [details, setDetails] = useState<RequestDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>("");

  const loginRedirect = useMemo(() => {
    const params = new URLSearchParams({ requestId, userCode });
    return `/cli/authorize?${params.toString()}`;
  }, [requestId, userCode]);

  const activeOrganizationId = session?.session.activeOrganizationId ?? "";
  const approvalOrganizationId = activeOrganizationId || selectedOrganizationId;

  useEffect(() => {
    if (isPending) {
      return;
    }
    if (!session?.user) {
      navigate({
        to: "/login",
        search: {
          redirect: loginRedirect,
          mode: undefined,
          email: undefined,
        },
      });
    }
  }, [isPending, loginRedirect, navigate, session?.user]);

  useEffect(() => {
    if (!session?.user) {
      return;
    }

    async function loadOrganizations() {
      if (activeOrganizationId) {
        setSelectedOrganizationId(activeOrganizationId);
        return;
      }

      setIsLoadingOrganizations(true);
      try {
        const { data: organizations } = await authClient.organization.list();
        if (!organizations || organizations.length === 0) {
          setError("No active organization is available for this account.");
          setOrganizations([]);
          setSelectedOrganizationId("");
          return;
        }

        const organizationOptions = organizations.map((organization) => ({
          id: organization.id,
          name: organization.name || organization.slug || organization.id,
        }));
        setOrganizations(organizationOptions);
        setSelectedOrganizationId(
          organizationOptions.length === 1 ? organizationOptions[0].id : "",
        );
      } catch {
        setError("Failed to load organizations for this account.");
        setOrganizations([]);
        setSelectedOrganizationId("");
      } finally {
        setIsLoadingOrganizations(false);
      }
    }

    void loadOrganizations();
  }, [activeOrganizationId, session?.user]);

  useEffect(() => {
    if (!session?.user || !requestId || !userCode) {
      return;
    }

    async function loadDetails() {
      setError(null);
      const response = await fetch(
        buildRequestApiUrl({ path: "", requestId, userCode }),
        { credentials: "include" },
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
        setError(
          typeof payload?.error === "string"
            ? payload.error
            : "Failed to load CLI authorization request.",
        );
        return;
      }

      const payload = (await response.json()) as RequestDetails;
      setDetails(payload);
    }

    void loadDetails();
  }, [requestId, session?.user, userCode]);

  async function submitAction(args: { action: "approve" | "reject" }) {
    if (!requestId || !userCode) {
      setError("Missing CLI authorization details.");
      return;
    }

    if (args.action === "approve" && !approvalOrganizationId) {
      setError("Choose an organization before approving CLI access.");
      return;
    }

    setIsActing(true);
    setError(null);
    try {
      const response = await fetch(
        buildRequestApiUrl({ path: `/${args.action}`, requestId, userCode }),
        {
          body:
            args.action === "approve"
              ? JSON.stringify({ organizationId: approvalOrganizationId })
              : undefined,
          credentials: "include",
          headers:
            args.action === "approve"
              ? { "Content-Type": "application/json" }
              : undefined,
          method: "POST",
        },
      );
      const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;
      const resolvedStatus = extractApprovalStatus({ payload });
      if (!response.ok && !resolvedStatus) {
        setError(
          typeof payload?.error === "string"
            ? payload.error
            : `Failed to ${args.action} CLI authorization request.`,
        );
        return;
      }

      setDetails((current) =>
        current
          ? {
              ...current,
              status: resolvedStatus ?? current.status,
            }
          : current,
      );
    } finally {
      setIsActing(false);
    }
  }

  if (!requestId || !userCode) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090b] px-6 text-zinc-100">
        <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8">
          <h1 className="text-lg font-semibold">Invalid CLI authorization link</h1>
          <p className="mt-3 text-sm text-zinc-400">
            This approval link is missing the request details needed to continue.
          </p>
        </div>
      </div>
    );
  }

  const machineName = details?.requestedMachineName?.trim() || "this machine";
  const status = details?.status ?? "pending";
  const shouldChooseOrganization = !!session?.user && !activeOrganizationId;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#09090b] px-6 text-zinc-100">
      <div className="w-full max-w-lg rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 shadow-2xl shadow-black/40">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500">
          Cord CLI
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-[-0.02em] text-white">
          Authorize {machineName}
        </h1>
        <p className="mt-3 text-sm leading-6 text-zinc-400">
          Approve this device to let the Cord CLI manage your account and provider connections.
        </p>

        <div className="mt-6 rounded-xl border border-white/[0.08] bg-black/20 p-4 text-sm text-zinc-300">
          <div className="flex items-center justify-between gap-4">
            <span className="text-zinc-500">Request status</span>
            <span className="font-medium capitalize text-white">{status}</span>
          </div>
          {details?.expiresAt ? (
            <div className="mt-2 flex items-center justify-between gap-4">
              <span className="text-zinc-500">Expires</span>
              <span>{new Date(details.expiresAt).toLocaleString()}</span>
            </div>
          ) : null}
        </div>

        {shouldChooseOrganization ? (
          <div className="mt-4 rounded-xl border border-white/[0.08] bg-black/20 p-4 text-sm text-zinc-300">
            <div className="flex flex-col gap-2">
              <span className="text-zinc-500">Organization</span>
              <p className="text-sm leading-6 text-zinc-400">
                This browser session does not have an active organization yet, so choose which workspace this CLI login should use.
              </p>
              <select
                value={selectedOrganizationId}
                onChange={(event) => setSelectedOrganizationId(event.target.value)}
                disabled={isLoadingOrganizations || isActing}
                className="mt-2 rounded-lg border border-white/[0.12] bg-[#0f0f12] px-3 py-2 text-sm text-white outline-none transition focus:border-white/[0.24] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">
                  {isLoadingOrganizations ? "Loading organizations…" : "Choose an organization"}
                </option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : null}

        {error ? (
          <p className="mt-5 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        ) : null}

        {status === "pending" ? (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => void submitAction({ action: "approve" })}
              disabled={isActing || !details || (shouldChooseOrganization && !approvalOrganizationId)}
              className="inline-flex flex-1 items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-semibold text-zinc-950 transition hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Approve CLI access
            </button>
            <button
              type="button"
              onClick={() => void submitAction({ action: "reject" })}
              disabled={isActing || !details}
              className="inline-flex flex-1 items-center justify-center rounded-xl border border-white/[0.1] bg-transparent px-4 py-3 text-sm font-semibold text-zinc-200 transition hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-zinc-400">
            {status === "approved"
              ? "Authorization complete. You can return to your terminal."
              : status === "rejected"
                ? "This CLI authorization request was rejected."
                : "This CLI authorization request has expired."}
          </p>
        )}
      </div>
    </div>
  );
}
